/**
 * SignalDecisionDrawer — shows the matcher_decisions trace for one
 * signal: why it traded (or didn't).
 *
 * Visible sections:
 *
 *   1. Outcome banner — single-line tone (matched / filtered /
 *      cooldown / no_strategy_eligible / …).
 *   2. Counts        — candidates_considered, my-strategy rejections.
 *   3. Rejections    — list of {filter, detail, stage} for the caller's
 *                      own strategies only (admin trace is hidden).
 *   4. Position link — if matched and a position opened, show the
 *      realized outcome (pnl_pct / exit_reason / closed_at).
 *
 * If no decision row exists (matcher hasn't seen the signal yet, or
 * it's pre-matcher-deployment), we render "no trace" with the timestamp.
 */
import { useTranslation } from 'react-i18next'
import { Drawer } from '@/components/ui/Drawer'
import {
  type RejectionEntry,
  type SignalDecision,
  useSignalDecision,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatPct } from '@/lib/format'

interface SignalDecisionDrawerProps {
  signalId: number | null
  signalLabel?: string
  onClose: () => void
}

const OUTCOME_TONE: Record<string, 'pulse' | 'rose' | 'amber' | 'sky' | 'neutral'> = {
  matched:              'pulse',
  no_strategy_eligible: 'neutral',
  all_filtered:         'amber',
  cooldown:             'sky',
  daily_limit:          'amber',
  concurrent_limit:     'amber',
  no_breadth_data:      'neutral',
  breadth_blocked:      'amber',
  flood_skip:           'sky',
  error:                'rose',
}

export function SignalDecisionDrawer({
  signalId, signalLabel, onClose,
}: SignalDecisionDrawerProps) {
  const { t } = useTranslation('app')
  const { data, isLoading, error } = useSignalDecision(signalId)

  return (
    <Drawer
      open={signalId != null}
      onClose={onClose}
      title={signalLabel ?? t('signal_decision.title')}
      subtitle={
        data
          ? `#${signalId} · ${new Date(data.decided_at).toLocaleString()}`
          : signalId
          ? `#${signalId}`
          : undefined
      }
    >
      {isLoading ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : error || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {t('signal_decision.no_trace')}
        </div>
      ) : (
        <div className="px-5 py-4 space-y-6">
          <OutcomeBanner decision={data} />
          <Counts decision={data} />
          <Rejections rejections={data.rejections_mine} />
          {data.opened_position_id && (
            <Outcome decision={data} />
          )}
        </div>
      )}
    </Drawer>
  )
}

/* ─── Outcome banner ─────────────────────────────────────────────── */

function OutcomeBanner({ decision }: { decision: SignalDecision }) {
  const { t } = useTranslation('app')
  const tone = OUTCOME_TONE[decision.final_outcome] ?? 'neutral'
  return (
    <div className={cn(
      'px-3 py-3 border',
      tone === 'pulse'   && 'bg-pulse-faint border-pulse/40',
      tone === 'rose'    && 'bg-rose-faint border-rose/40',
      tone === 'amber'   && 'bg-amber-faint border-amber/40',
      tone === 'sky'     && 'bg-sky-faint border-sky/40',
      tone === 'neutral' && 'bg-elevated border-line',
    )}>
      <div className="text-xs uppercase tracking-wider text-textFaint">
        {t('signal_decision.outcome')}
      </div>
      <div className={cn(
        'mt-1 text-sm font-semibold',
        tone === 'pulse' && 'text-pulse',
        tone === 'rose'  && 'text-rose',
        tone === 'amber' && 'text-amber',
        tone === 'sky'   && 'text-sky',
      )}>
        {t(`final_outcome.${decision.final_outcome}`, {
          defaultValue: decision.final_outcome,
        })}
      </div>
      {decision.final_rejection_reason && (
        <div className="mt-1 text-xs text-textDim">
          {decision.final_rejection_reason}
        </div>
      )}
    </div>
  )
}

/* ─── Counts ────────────────────────────────────────────────────── */

function Counts({ decision }: { decision: SignalDecision }) {
  const { t } = useTranslation('app')
  return (
    <div className="grid grid-cols-2 gap-3 font-mono">
      <CountBox
        label={t('signal_decision.candidates')}
        value={decision.candidates_considered}
      />
      <CountBox
        label={t('signal_decision.rejections_mine')}
        value={decision.rejections_mine.length}
      />
    </div>
  )
}

function CountBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-ink border border-line p-3">
      <div className="text-textFaint text-2xs uppercase tracking-wider">
        {label}
      </div>
      <div className="mt-1 text-lg tabular-nums">{value}</div>
    </div>
  )
}

/* ─── Rejections list ───────────────────────────────────────────── */

function Rejections({ rejections }: { rejections: RejectionEntry[] }) {
  const { t } = useTranslation('app')
  if (rejections.length === 0) {
    return (
      <Section title={t('signal_decision.rejections_section')}>
        <p className="px-3 py-3 text-xs text-textFaint">
          {t('signal_decision.no_rejections')}
        </p>
      </Section>
    )
  }
  return (
    <Section title={t('signal_decision.rejections_section')}>
      <ul className="divide-y divide-line/40">
        {rejections.map((r, idx) => (
          <li key={idx} className="px-3 py-2 text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="pill bg-rose-faint text-rose">
                {String(r.filter ?? '?')}
              </span>
              {r.strategy_id != null && (
                <span className="text-textFaint">#{r.strategy_id}</span>
              )}
              {r.stage && (
                <span className="ml-auto text-textFaint">{r.stage}</span>
              )}
            </div>
            {r.detail && (
              <div className="mt-1 text-textDim break-words">{String(r.detail)}</div>
            )}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ─── Position outcome ──────────────────────────────────────────── */

function Outcome({ decision }: { decision: SignalDecision }) {
  const { t } = useTranslation('app')
  return (
    <Section title={t('signal_decision.matched_section')}>
      <div className="px-3 py-2 text-xs font-mono space-y-1">
        <Row
          label={t('signal_decision.strategy')}
          value={`#${decision.matched_strategy_id ?? '—'}`}
        />
        <Row
          label={t('signal_decision.position')}
          value={`#${decision.opened_position_id ?? '—'}`}
        />
        {decision.realized_pnl_pct != null && (
          <Row
            label={t('signal_decision.realized')}
            value={formatPct(decision.realized_pnl_pct, { sign: true })}
            valueClass={
              decision.realized_pnl_pct >= 0 ? 'text-pulse' : 'text-rose'
            }
          />
        )}
        {decision.realized_exit_reason && (
          <Row
            label={t('position_detail.exit_reason')}
            value={t(`exit_reason.${decision.realized_exit_reason}`, {
              defaultValue: decision.realized_exit_reason,
            })}
          />
        )}
        {decision.position_closed_at && (
          <Row
            label={t('position_detail.closed_at')}
            value={new Date(decision.position_closed_at).toLocaleString()}
          />
        )}
      </div>
    </Section>
  )
}

/* ─── building blocks ───────────────────────────────────────────── */

function Section({
  title, children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h3 className="text-textFaint text-xs uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="border border-line bg-ink">{children}</div>
    </section>
  )
}

function Row({
  label, value, valueClass,
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center">
      <span className="text-textDim">{label}</span>
      <span className={cn('ml-auto', valueClass ?? 'text-text')}>
        {value}
      </span>
    </div>
  )
}
